import axios from "./axiosInstance";
import config from "./config";
import { captureException } from "../utils/sentry";

const BASE_URL = config.BASE_URL;

export const createMemberShip = (newForm) => {
  return axios
    .post(`${BASE_URL}/memberships`, newForm, {
      headers: {
        "Content-Type": "application/json",
      },
    })
    .then((res) => {
      if (res) return res;
    })
    .catch((err) => {

      throw err; // Re-throw so caller can handle and show proper error message
    });
};
// reactivate mamber
export const reactivateMembership = (token, id, newForm) => {
  return axios
    .post(`${BASE_URL}/memberships/${id}/reactivate`, newForm, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// GET - what reactivating this family would charge under the club's fee rule
// (the annual fee cadence in Settings), without writing anything.
// 200 → { success, data: { charge, amount, reason } }. Throws on failure so
// the caller can say it could not tell, rather than guess a number.
export const getReactivationFeeQuote = (token, id) => {
  return axios
    .get(`${BASE_URL}/memberships/${id}/reactivation-fee`, {
      headers: { authorization: `Bearer ${token}` },
    })
    .then((res) => res?.data)
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

export const updateMembershipDetails = (token, id, newForm) => {
  return axios
    .put(`${BASE_URL}/memberships/${id}`, newForm, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// Partial update for auto-save
export const patchMembershipDetails = (token, id, updates) => {
  return axios
    .patch(`${BASE_URL}/memberships/${id}`, updates, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {

      throw err; // Re-throw for auto-save error handling
    });
};

// Toggle SMS opt-in / opt-out via the dedicated endpoint that
// server-stamps the timestamp + consent version. Body: { opt_in: boolean }.
export const setMembershipSmsOptIn = (token, id, optIn) => {
  return axios
    .post(`${BASE_URL}/memberships/${id}/sms-opt-in`, { opt_in: !!optIn }, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => res?.data)
    .catch((err) => ({ success: false, error: err?.response?.data?.error || err.message }));
};

// Get membership by ID
export const checkEmail = (form) => {
  return axios
    .post(`${BASE_URL}/memberships/check-email`, form, {
      headers: {
        "Content-Type": "application/json",
      },
    })
    .then((res) => {
      if (res) return res;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// Get membership by ID
export const getAllMemberships = (
  token,
  {
    page = 1,
    limit = 20,
    searchTerm = "",
    sortField = "first_name",
    sortOrder = "asc",
    programs = "",
    status = "all",
    verificationFilter = "",
    // "member" (default), "lead", "customer", a comma-separated subset, or
    // "all". Defaulted here so every existing caller keeps the member roster.
    accountTypes = "member",
    // Leads only: "open", "archived" or "all". The backend ignores it for any
    // other account type, so the default never narrows the member roster.
    leadStatus = "all",
    // Leads only: the days (YYYY-MM-DD) a lead was archived between. Either
    // may be blank. The backend reads them on the club's calendar.
    leadArchivedFrom = "",
    leadArchivedTo = "",
  }
) => {
  return axios
    .get(`${BASE_URL}/memberships/all/members`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
      params: {
        page,
        limit,
        searchTerm,
        sortField,
        sortOrder,
        programs,
        status,
        verificationFilter,
        accountTypes,
        leadStatus,
        // Sent only when set, so every other caller's request is unchanged.
        ...(leadArchivedFrom ? { leadArchivedFrom } : {}),
        ...(leadArchivedTo ? { leadArchivedTo } : {}),
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// Get membership by ID
export const getMembersInvoicesForMonth = (
  token,
  {
    month,
    page = 1,
    limit = 20,
    searchTerm = "",
    statusFilter = "Not Generated",
  }
) => {
  return axios
    .get(`${BASE_URL}/memberships/invoices-for-month`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
      params: { month, page, limit, searchTerm, statusFilter },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// Get membership by ID. Pass an optional explicit membershipId to
// fetch a specific one the caller has access to (multi-family
// switcher path). Without it, the backend picks the caller's primary
// membership, falling back to the first secondary.
/**
 * @param {string|null} token
 * @param {string|null|undefined} [membershipId]
 */
export const getMembership = (token, membershipId) => {
  const url = membershipId
    ? `${BASE_URL}/memberships?membership_id=${encodeURIComponent(membershipId)}`
    : `${BASE_URL}/memberships`;
  return axios
    .get(url, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// List every membership the caller has access to (primary + active
// secondaries). Used by the multi-family switcher in the nav.
export const getMyMembershipsList = (token) => {
  return axios
    .get(`${BASE_URL}/memberships/me/list`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => res?.data || { success: false, data: [] })
    .catch((err) => { captureException(err); return { success: false, data: [] }; });
};

// Get membership by ID
export const checkMembershipExpiry = (token) => {
  return axios
    .get(`${BASE_URL}/memberships/check/expiry`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

export const updateMembershipAutoPay = (token, id, newForm) => {
  return axios
    .patch(`${BASE_URL}/memberships/autopay/${id}`, newForm, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

export const getMemberById = (token, id) => {
  // Defensive guard — CLUB-CENTRAL-TENANT-3T (2026-06-03) was caused
  // by a caller firing this on mount before Redux had hydrated the
  // membership id, which interpolated as the literal string
  // "undefined" into the URL and produced a real 404. Caller-side
  // fix is the actual remediation; this just stops the URL from
  // being malformed if a new caller forgets the same guard.
  if (!id || id === "undefined" || id === "null") {
    return Promise.resolve(undefined);
  }
  return axios
    .get(`${BASE_URL}/memberships/${id}`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// Get memberships with negative balances
// Options: { excludeArchived?: boolean, excludeInactive?: boolean }
export const getNegativeBalanceMemberships = (token, options = {}) => {
  const params = new URLSearchParams();
  if (options.excludeArchived) {
    params.append("excludeArchived", "true");
  }
  if (options.excludeInactive) {
    params.append("excludeInactive", "true");
  }
  const queryString = params.toString();
  const url = `${BASE_URL}/memberships/negative-balances${queryString ? `?${queryString}` : ""}`;

  return axios
    .get(url, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {

      throw err; // Re-throw so component can handle it
    });
};

// Mark an invoice as generated (creates a $0 marker invoice)
export const markInvoiceGenerated = (token, { membershipId, month }) => {
  return axios
    .post(`${BASE_URL}/memberships/mark-invoice-generated`, { membershipId, month }, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// Schedule a future cancellation for a membership
export const scheduleMembershipCancellation = (token, id, { cancellation_date, reason, follow_up_task_date, follow_up_assigned_to }) => {
  return axios
    .post(`${BASE_URL}/memberships/${id}/schedule-cancellation`, { cancellation_date, reason, follow_up_task_date, follow_up_assigned_to }, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// Cancel a previously scheduled cancellation
export const cancelScheduledCancellation = (token, id) => {
  return axios
    .post(`${BASE_URL}/memberships/${id}/cancel-scheduled-cancellation`, {}, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// Convert a customer account to a full member (account_type flip is a
// privilege boundary — dedicated endpoint, not PATCH-able).
// options: { start_date, bill_registration_fee, registration_fee_amount,
//            payment_mode: 'publish_invoice'|'charge_card'|'desk',
//            send_welcome_email }
export const convertCustomerToMember = (token, membershipId, options = {}) => {
  return axios
    .post(`${BASE_URL}/memberships/${membershipId}/convert-to-member`, options, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// Recalculate membership balance
export const recalculateMembershipBalance = (token, membershipId) => {
  return axios
    .post(`${BASE_URL}/memberships/${membershipId}/recalculate-balance`, {}, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {

      throw err; // Re-throw so component can handle it
    });
};

// What a hard delete would destroy — real pre-delete counts, including the
// second-order athlete cascade (class enrollments, attendance) that the Fair
// cleanup lost invisibly (2026-08-28). Same permission gate as the delete.
export const getMembershipDeleteImpact = (token, membershipId) => {
  return axios
    .get(`${BASE_URL}/memberships/${membershipId}/delete-impact`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => res?.data)
    .catch((err) => {
      throw err;
    });
};

// Hard-delete a membership and all related records. Permission-gated on the
// backend (hard_delete_memberships). Returns { related_counts } on success.
export const hardDeleteMembership = (token, membershipId, reason) => {
  return axios
    .delete(`${BASE_URL}/memberships/${membershipId}`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
      data: { reason: reason || null },
    })
    .then((res) => res?.data)
    .catch((err) => {
      throw err;
    });
};

// Merge a duplicate membership INTO this membership (keeperId survives, the
// duplicate is removed). Permission-gated on the backend
// (hard_delete_memberships). dryRun=true previews the merge — the returned
// summary has moved-record counts, balance impact, and likely-duplicate
// athletes — without changing anything.
export const mergeDuplicateMembership = (
  token,
  keeperId,
  duplicateMembershipId,
  opts = {}
) => {
  const dryRun = opts.dryRun !== false;
  const reason = opts.reason || null;
  return axios
    .post(
      `${BASE_URL}/memberships/${keeperId}/merge-duplicate`,
      {
        duplicate_membership_id: duplicateMembershipId,
        dry_run: dryRun,
        reason,
      },
      {
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
      }
    )
    .then((res) => res?.data)
    .catch((err) => {
      throw err;
    });
};

// Combined verification + suppression state for the membership's
// primary email. Drives the "Email blocked — Unblock" pill on the
// edit-membership page.
export const getEmailDeliverability = (token, membershipId) => {
  return axios
    .get(`${BASE_URL}/memberships/${membershipId}/email-deliverability`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => res?.data)
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// Active program enrollments for a membership. Used by billing
// history to show "what are these dues FOR?" context (athlete +
// program + schedule) inline so staff doesn't have to switch tabs
// to verify billed-as-1-hr vs billed-as-2-hr.
export const getActiveEnrollments = (token, membershipId) => {
  return axios
    .get(`${BASE_URL}/memberships/${membershipId}/active-enrollments`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => res?.data)
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// Staff override: clear the suppression entry so sends (including
// password-reset emails) flow to this address again. Writes an
// account_notes audit row.
export const releaseEmailSuppression = (token, membershipId, reason) => {
  return axios
    .post(
      `${BASE_URL}/memberships/${membershipId}/release-email-suppression`,
      { reason: reason || null },
      {
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
      },
    )
    .then((res) => res?.data)
    .catch((err) => {
      throw err;
    });
};

// Staff block: stop every send to the membership's primary email, with a
// reason (PM 832c67d8). The counterpart of releaseEmailSuppression; the
// server writes the club's suppression list and an account_notes entry.
export const blockEmail = (token, membershipId, reason) => {
  return axios
    .post(
      `${BASE_URL}/memberships/${membershipId}/block-email`,
      { reason },
      {
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
      },
    )
    .then((res) => res?.data)
    .catch((err) => {
      throw err;
    });
};

// ── Additional contacts, one at a time ──────────────────────────────────────
// The membership PUT above takes the WHOLE contacts list and the server
// deletes whatever is missing from it, so a page holding an older copy of
// the list deleted contacts added elsewhere. These three touch one contact
// each. Every answer carries `data.contacts`: the family's full list as it
// is now. Use that to refresh the page; never rebuild it from a local copy.
const contactHeaders = (token) => ({
  "Content-Type": "application/json",
  authorization: `Bearer ${token}`,
});

export const addMembershipContact = (token, membershipId, contact) =>
  axios
    .post(`${BASE_URL}/memberships/${membershipId}/contacts`, contact, { headers: contactHeaders(token) })
    .then((res) => res?.data);

export const updateMembershipContact = (token, membershipId, contactId, changes) =>
  axios
    .patch(`${BASE_URL}/memberships/${membershipId}/contacts/${contactId}`, changes, {
      headers: contactHeaders(token),
    })
    .then((res) => res?.data);

export const removeMembershipContact = (token, membershipId, contactId) =>
  axios
    .delete(`${BASE_URL}/memberships/${membershipId}/contacts/${contactId}`, { headers: contactHeaders(token) })
    .then((res) => res?.data);
